/**
 * Dynamic command registration.
 *
 * Derives service grouping from the `@join__field(graph: ...)` directives
 * that the router injects into introspection responses, then builds
 * Commander.js commands at runtime.
 */
import { Command } from 'commander';
import {
  isEnumType,
  isInputObjectType,
  isInterfaceType,
  isListType,
  isNonNullType,
  isObjectType,
  isScalarType,
  isUnionType,
  type GraphQLArgument,
  type GraphQLEnumType,
  type GraphQLField,
  type GraphQLInputObjectType,
  type GraphQLInputType,
  type GraphQLNamedType,
  type GraphQLObjectType,
  type GraphQLOutputType,
  type GraphQLSchema,
} from 'graphql';

import { graphqlRaw } from '../graphql/client.ts';
import { type FieldServiceMap } from '../graphql/introspection.ts';
import { buildQuery, UnknownFieldError } from '../graphql/query-builder.ts';
import { actingAsApplication } from '../lib/application-session.ts';
import { resolveCompanyId } from '../lib/company.ts';
import { getUserContext } from '../lib/config.ts';
import { error, hint, info, isJsonMode, printJson, warn } from '../lib/output.ts';
import { formatResult } from '../lib/render.ts';

// Context fields that are auto-injected from ~/.sortlist/config.json.
// Schema field names (key) → UserContext property (value). The CLI is
// company-scoped; marketplace fields like `agencyId` are NOT auto-filled
// (Agency and Company are independent entities) and must be passed
// explicitly via `--agency-id`.
// Only `companyId`. An identifier naming *somebody else* must never come from the
// caller's own context: filling the old `oauthId` argument that way made
// `sl invite-company-member` silently invite the caller to their own company.
const CONTEXT_FIELDS: Record<string, string> = {
  companyId: 'companyId',
};

// Fields to skip — internal relay/federation helpers
const SKIP_FIELDS = new Set(['node', 'nodes', 'id', '_entities', '_service']);

function unwrap(type: GraphQLOutputType | GraphQLInputType): GraphQLNamedType {
  if (isNonNullType(type)) return unwrap(type.ofType as GraphQLOutputType | GraphQLInputType);
  if (isListType(type)) return unwrap(type.ofType as GraphQLOutputType | GraphQLInputType);
  return type as GraphQLNamedType;
}

function kebab(s: string): string {
  return s.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
}

/**
 * For mutations with a single `input` argument of an Input type,
 * return the fields of that input type for flattening into CLI options.
 */
interface CliArg {
  name: string;
  type: GraphQLArgument['type'];
  required: boolean;
  description?: string | null;
}

function getInputFields(field: GraphQLField<unknown, unknown>): CliArg[] | null {
  if (field.args.length !== 1) return null;
  const arg = field.args[0];
  if (arg.name !== 'input') return null;
  const argType = unwrap(arg.type);
  if (!argType || !isInputObjectType(argType)) return null;
  const fields = argType.getFields();
  return Object.entries(fields).map(([name, f]) => ({
    name,
    type: f.type,
    required: isNonNullType(f.type),
    description: f.description,
  }));
}

/**
 * Render a GraphQL type as its SDL signature, e.g. `String!`,
 * `[OrganizationSortInput!]`, `OrganizationFilterInput`. Works for both input
 * and output types (the wrapper structure is identical).
 */
function renderType(type: GraphQLInputType | GraphQLOutputType): string {
  if (isNonNullType(type)) return `${renderType(type.ofType as GraphQLInputType | GraphQLOutputType)}!`;
  if (isListType(type)) return `[${renderType(type.ofType as GraphQLInputType | GraphQLOutputType)}]`;
  return (type as GraphQLNamedType).name;
}

/**
 * A "structured" arg is one that must be supplied as JSON on the CLI and parsed
 * (rather than passed through as a raw string): lists and input objects. Plain
 * leaf scalars and enums are passed as raw strings — including when wrapped in
 * non-null (`String!` is still a bare string on the CLI). The non-null wrapper
 * is transparent for this decision; a list at any level means JSON.
 */
function isStructuredInput(type: GraphQLInputType): boolean {
  if (isNonNullType(type)) return isStructuredInput(type.ofType as GraphQLInputType);
  if (isListType(type)) return true;
  return isInputObjectType(type);
}

/**
 * Coerce a raw CLI string into the value GraphQL expects for the arg's type.
 * Leaf scalars are parsed by name; structured types (input objects, lists,
 * enums) are parsed as JSON. Throws a readable error on malformed JSON so the
 * action can surface it instead of silently sending a bad string.
 */
/** `[Enum!]` however it is wrapped: the one structured input a person can type by hand. */
function enumListOf(type: GraphQLInputType): GraphQLEnumType | null {
  const listless = isNonNullType(type) ? type.ofType : type;
  if (!isListType(listless)) return null;
  const inner = unwrap(listless.ofType as GraphQLInputType);
  return isEnumType(inner) ? (inner as GraphQLEnumType) : null;
}

function enumOf(type: GraphQLInputType): GraphQLEnumType | null {
  const listless = isNonNullType(type) ? (type.ofType as GraphQLInputType) : type;
  if (isListType(listless)) return null;
  return isEnumType(listless) ? (listless as GraphQLEnumType) : null;
}

function commonPrefix(values: string[]): string {
  if (values.length < 2) return '';
  let prefix = values[0];
  for (const value of values) while (!value.startsWith(prefix)) prefix = prefix.slice(0, -1);
  const cut = prefix.lastIndexOf('_');
  return cut === -1 ? '' : prefix.slice(0, cut + 1);
}

function matchEnumValue(part: string, names: string[], prefix: string): string | undefined {
  const wanted = part
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .toUpperCase();
  return names.find((name) => name === wanted || name === `${prefix}${wanted}`);
}

function spellableValues(names: string[], prefix: string): string[] {
  return names.map((name) =>
    name
      .slice(prefix.length)
      .toLowerCase()
      .replace(/_([a-z])/g, (_, letter: string) => letter.toUpperCase()),
  );
}

/**
 * `emailAddress,phone`, `all` or `none` for an enum list, so a caller never types JSON
 * for a handful of known names. Case-insensitive, camelCase reads as SNAKE_CASE
 * so a value can be spelt like the field it stands for, and a prefix every value
 * shares (`CONTACT_`) may be left out. A value starting with `[` is still JSON.
 */
export function coerceEnumList(raw: string, enumType: GraphQLEnumType, flagName: string): string[] {
  const names = enumType.getValues().map((value) => value.name);
  if (raw.trim() === 'all') return names;
  if (raw.trim() === 'none') return [];

  const prefix = commonPrefix(names);
  return raw
    .split(',')
    .filter((part) => part.trim() !== '')
    .map((part) => {
      const match = matchEnumValue(part, names, prefix);
      if (match === undefined) {
        throw new Error(
          `--${flagName} does not know "${part.trim()}". ` +
            `One or more of: ${spellableValues(names, prefix).join(', ')}, all, or none.`,
        );
      }
      return match;
    });
}

export function coerceEnum(raw: string, enumType: GraphQLEnumType, flagName: string): string {
  const names = enumType.getValues().map((value) => value.name);
  const prefix = commonPrefix(names);
  const match = matchEnumValue(raw, names, prefix);
  if (match === undefined) {
    throw new Error(
      `--${flagName} does not know "${raw.trim()}". One of: ${spellableValues(names, prefix).join(', ')}.`,
    );
  }
  return match;
}

export function coerceValue(raw: string, type: GraphQLInputType, flagName: string): unknown {
  const enumList = enumListOf(type);
  if (enumList && !raw.trim().startsWith('[')) return coerceEnumList(raw, enumList, flagName);

  const enumValue = enumOf(type);
  if (enumValue) return coerceEnum(raw, enumValue, flagName);

  if (isStructuredInput(type)) {
    try {
      return JSON.parse(raw);
    } catch (parseError) {
      throw new Error(
        `--${flagName} expects JSON for type ${renderType(type)}, but the value could not be parsed: ` +
          `${(parseError as Error).message}`,
      );
    }
  }

  const typeName = unwrap(type).name;
  if (typeName === 'Int') {
    const parsed = parseInt(raw, 10);
    if (Number.isNaN(parsed)) throw new Error(`--${flagName} expects an integer, got "${raw}".`);
    return parsed;
  }
  if (typeName === 'Float') {
    const parsed = parseFloat(raw);
    if (Number.isNaN(parsed)) throw new Error(`--${flagName} expects a number, got "${raw}".`);
    return parsed;
  }
  if (typeName === 'Boolean') {
    if (raw !== 'true' && raw !== 'false') {
      throw new Error(`--${flagName} expects "true" or "false", got "${raw}".`);
    }
    return raw === 'true';
  }
  return raw;
}

/**
 * One-line summary of an input-object or enum type's shape, used in option
 * help so callers know what JSON to pass without a separate introspection
 * round-trip. Expands a single level: `{ industry: StringFilterInput, country:
 * String, ... }` for input objects, `one of: A | B | C` for enums.
 */
function describeStructuredType(named: GraphQLNamedType): string | null {
  if (isInputObjectType(named)) {
    const fields = Object.values((named as GraphQLInputObjectType).getFields());
    const parts = fields.map((f) => `${f.name}: ${renderType(f.type)}`);
    return `{ ${parts.join(', ')} }`;
  }
  if (isEnumType(named)) {
    const values = (named as GraphQLEnumType).getValues().map((v) => v.name);
    return `one of: ${values.join(' | ')}`;
  }
  return null;
}

/** A named type worth pointing the user at via `sl schema inspect` — i.e. one with structure. */
function isInspectable(named: GraphQLNamedType): boolean {
  return isInputObjectType(named) || isEnumType(named) || isObjectType(named);
}

function oneLineDescription(text: string | null | undefined): string {
  return (text ?? '').replace(/\s+/g, ' ').trim();
}

/**
 * Render a full, human-readable description of a named type for
 * `sl schema inspect <Type>`: its kind, description, and one level of
 * structure (object/input fields with their types + descriptions, or enum
 * values). Nested structured types are named so the caller can `inspect` them
 * in turn. Returns null if the type does not exist in the schema.
 */
export function formatTypeInspection(schema: GraphQLSchema, typeName: string): string | null {
  const named = schema.getType(typeName);
  if (!named) return null;

  const lines: string[] = [];
  const suffix = oneLineDescription(named.description);
  const header = (kind: string): string => {
    const label = kind ? `${kind} ${named.name}` : named.name;
    return suffix ? `${label} — ${suffix}` : label;
  };

  const pushFields = (entries: Array<{ name: string; typeSig: string; description?: string | null }>): void => {
    const width = Math.max(...entries.map((entry) => `${entry.name}: ${entry.typeSig}`.length), 0);
    for (const entry of entries) {
      const signature = `${entry.name}: ${entry.typeSig}`;
      const note = oneLineDescription(entry.description);
      lines.push(note ? `  ${signature.padEnd(width)}   ${note}` : `  ${signature}`);
    }
  };

  if (isInputObjectType(named) || isObjectType(named)) {
    lines.push(header(isInputObjectType(named) ? 'input' : 'type'));
    pushFields(
      Object.values(named.getFields()).map((f) => ({
        name: f.name,
        typeSig: renderType(f.type),
        description: f.description,
      })),
    );
  } else if (isEnumType(named)) {
    lines.push(header('enum'));
    for (const value of named.getValues()) {
      const note = oneLineDescription(value.description);
      lines.push(note ? `  ${value.name.padEnd(20)}   ${note}` : `  ${value.name}`);
    }
  } else if (isScalarType(named)) {
    lines.push(header('scalar'));
    lines.push('  (scalar — pass a plain value)');
  } else {
    lines.push(header(''));
  }

  // Point the caller at the nested types they can drill into next.
  const nested = new Set<string>();
  if (isInputObjectType(named) || isObjectType(named)) {
    for (const field of Object.values(named.getFields())) {
      const fieldType = unwrap(field.type);
      if (isInspectable(fieldType) && fieldType.name !== named.name) nested.add(fieldType.name);
    }
  }
  if (nested.size > 0) {
    lines.push('');
    lines.push(
      `Inspect nested types: ${[...nested]
        .sort()
        .map((n) => `sl schema inspect ${n}`)
        .join(', ')}`,
    );
  }

  return lines.join('\n');
}

/**
 * Build the help description for a single CLI option from its GraphQL arg:
 * the field description (if any), the rendered type with a `(JSON)` marker for
 * structured inputs, and a one-level shape hint (input-object fields or enum
 * values) so callers know what to pass without a separate introspection step.
 */
function describeArg(arg: { name: string; type: GraphQLInputType; description?: string | null }): string {
  const parts: string[] = [];
  if (arg.description) parts.push(arg.description.replace(/\s+/g, ' ').trim());

  const typeSig = renderType(arg.type);
  const enumList = enumListOf(arg.type);
  parts.push(
    enumList
      ? `${typeSig} (comma-separated, all, or none)`
      : isStructuredInput(arg.type)
        ? `${typeSig} (JSON)`
        : typeSig,
  );

  // Shape hint applies to enums (raw string) and input objects (JSON) alike.
  const named = unwrap(arg.type);
  const shape = describeStructuredType(named);
  if (shape) parts.push(shape);

  // Point at `schema inspect` so nested input/enum types can be drilled into
  // without resorting to raw introspection.
  if (isInputObjectType(named) || isEnumType(named)) {
    parts.push(`details: sl schema inspect ${named.name}`);
  }

  return parts.join(' — ');
}

function buildCommand(
  schema: GraphQLSchema,
  fieldName: string,
  kind: 'query' | 'mutation',
  description: string,
  service: string,
): Command {
  const rootType = kind === 'query' ? schema.getQueryType() : schema.getMutationType();
  if (!rootType) throw new Error(`No ${kind} type in schema`);

  const field = rootType.getFields()[fieldName];
  if (!field) throw new Error(`Unknown ${kind} field: ${fieldName}`);

  const inputFields = getInputFields(field);
  const useInputWrapper = inputFields != null;
  const cliArgs: CliArg[] = useInputWrapper
    ? inputFields
    : field.args.map((a) => ({
        name: a.name,
        type: a.type,
        required: isNonNullType(a.type),
        description: a.description,
      }));

  const cmd = new Command(kebab(fieldName)).description(description);

  // Only *required* company arguments are worth blocking on. An optional one
  // means the server has a sensible default — for instance the caller's personal
  // company — and refusing to call it would make the CLI stricter than the API.
  const requiresCompanyId = cliArgs.some((a) => a.name === 'companyId' && a.required);

  for (const a of cliArgs) {
    // Variadic for an enum list, so `--reveal identity, email` and `--reveal identity email` both read.
    const placeholder = enumListOf(a.type) ? 'a,b|all|none...' : isStructuredInput(a.type) ? 'json' : kebab(a.name);
    const flag = `--${kebab(a.name)} <${placeholder}>`;
    const isContext = a.name in CONTEXT_FIELDS;
    if (isContext) {
      cmd.option(flag, `${describeArg(a)} (from context if omitted)`);
    } else if (a.required) {
      cmd.requiredOption(flag, describeArg(a));
    } else {
      cmd.option(flag, describeArg(a));
    }
  }

  cmd
    .option('--depth <depth>', 'Selection depth (default: 0)', parseInt)
    .option('--fields <fields>', 'Comma-separated fields to include')
    .option('--exclude <fields>', 'Comma-separated fields to exclude')
    .option('--total-count', 'Also count every match, not just this page (slow on a broad filter)');

  cmd.action(async function (this: Command) {
    const opts = this.optsWithGlobals();
    const ctx = getUserContext();
    // Two different companies: the one the caller acts for (the header, checked
    // against the token) and the one an operation is about (its `companyId`
    // argument, checked by membership). Naming a company as an argument must not
    // make the whole request act for it.
    const acting = typeof opts.company === 'string' ? opts.company : undefined;
    const actingId = await resolveCompanyId({ company: acting, ctx });
    const argumentCompanyId =
      typeof opts.companyId === 'string' ? await resolveCompanyId({ company: opts.companyId, ctx }) : actingId;

    if (requiresCompanyId && !argumentCompanyId) {
      error('No company selected.');
      hint('Pass --company-id <slug|id>, or act for one with --company or `sl config set company <slug|id>`.');
      process.exit(2);
    }

    if (argumentCompanyId) opts.companyId = argumentCompanyId;

    const collectArgs = (): Record<string, unknown> => {
      const out: Record<string, unknown> = {};
      for (const a of cliArgs) {
        const raw = Array.isArray(opts[a.name]) ? (opts[a.name] as string[]).join(',') : opts[a.name];
        if (raw != null) {
          try {
            out[a.name] = coerceValue(raw, a.type, kebab(a.name));
          } catch (coerceError) {
            error((coerceError as Error).message);
            hint('Run the command with --help to see the expected shape of each argument.');
            process.exit(2);
          }
        }
      }
      return out;
    };

    const variables: Record<string, unknown> = useInputWrapper ? { input: collectArgs() } : collectArgs();

    let built: ReturnType<typeof buildQuery>;
    try {
      built = buildQuery(schema, kind, fieldName, variables, {
        depth: opts.depth,
        fields: opts.fields?.split(','),
        exclude: opts.exclude?.split(','),
        totalCount: opts.totalCount === true,
      });
    } catch (buildError) {
      if (!(buildError instanceof UnknownFieldError)) throw buildError;
      error(buildError.message);
      hint(`Did you mean: ${buildError.suggestions.join(', ')}?`);
      process.exit(2);
    }
    const { query, fieldName: fName } = built;
    const data = await graphqlRaw(query, variables, {
      endpoint: opts.endpoint,
      company: acting,
      companyId: actingId,
      subgraph: service,
    });
    const result = data[fName];
    if (result == null) {
      // Not "no results": the question does not apply to the caller.
      if (actingAsApplication()) {
        info(`${fieldName} answers for people, and you are acting as an application.`);
        hint('`sl whoami` reports what the token identifies; `sl use me` switches back.');
      } else {
        warn('No results returned.');
        hint('The query succeeded but the server returned null.');
        hint('Try different arguments or check that the resource exists.');
      }
    } else if (isJsonMode(opts)) {
      printJson(result);
    } else {
      const rendered = formatResult(result);
      if (rendered !== null) info(rendered);
    }
  });

  return cmd;
}

// ── Service map derivation ──

interface ServiceEntry {
  queries: Array<{ fieldName: string; description: string }>;
  mutations: Array<{ fieldName: string; description: string }>;
}

/**
 * Derive the service → operations map from the `@join__field(graph: ...)`
 * directives the router exposes on each root field. Fields without a mapped
 * subgraph are skipped.
 */
function deriveServiceMap(schema: GraphQLSchema, fieldServices: FieldServiceMap): Map<string, ServiceEntry> {
  const map = new Map<string, ServiceEntry>();

  function ensure(service: string): ServiceEntry {
    if (!map.has(service)) map.set(service, { queries: [], mutations: [] });
    return map.get(service)!;
  }

  const rootTypes: Array<{ kind: 'queries' | 'mutations'; type: GraphQLObjectType | null | undefined }> = [
    { kind: 'queries', type: schema.getQueryType() },
    { kind: 'mutations', type: schema.getMutationType() },
  ];

  for (const { kind, type } of rootTypes) {
    if (!type) continue;
    const services = fieldServices.get(type.name);
    if (!services) continue;

    for (const [fieldName, field] of Object.entries(type.getFields())) {
      if (SKIP_FIELDS.has(fieldName) || fieldName.startsWith('__')) continue;

      // Skip operations returning interfaces or unions
      const returnType = unwrap(field.type);
      if (isInterfaceType(returnType) || isUnionType(returnType)) continue;

      const service = services.get(fieldName);
      if (!service) continue;

      ensure(service)[kind].push({ fieldName, description: field.description ?? '' });
    }
  }

  // Sort fields alphabetically within each service
  for (const entry of map.values()) {
    entry.queries.sort((a, b) => a.fieldName.localeCompare(b.fieldName));
    entry.mutations.sort((a, b) => a.fieldName.localeCompare(b.fieldName));
  }

  return map;
}

/** One flattened operation, ready to register at the top level. */
export interface OperationCommand {
  command: Command;
  /** Field name as it appears on the supergraph root type. */
  fieldName: string;
  kind: 'query' | 'mutation';
  /** Subgraph that serves it. Shown when listing, never used as a command prefix. */
  service: string;
}

/**
 * Build one command per root field, flat.
 *
 * Operation names are unique across the whole supergraph, so grouping by
 * subgraph would only add a prefix a caller has to guess. The rare name shared
 * by a query and a mutation is suffixed instead. The subgraph is kept as
 * metadata for `sl operations`.
 */
export function buildOperationCommands(schema: GraphQLSchema, fieldServices: FieldServiceMap): OperationCommand[] {
  const serviceMap = deriveServiceMap(schema, fieldServices);
  const operations: OperationCommand[] = [];

  const nameCounts = new Map<string, number>();
  for (const entry of serviceMap.values()) {
    for (const op of [...entry.queries, ...entry.mutations]) {
      nameCounts.set(op.fieldName, (nameCounts.get(op.fieldName) ?? 0) + 1);
    }
  }

  for (const [service, entry] of serviceMap) {
    const kinds: Array<{ kind: 'query' | 'mutation'; ops: Array<{ fieldName: string; description: string }> }> = [
      { kind: 'query', ops: entry.queries },
      { kind: 'mutation', ops: entry.mutations },
    ];

    for (const { kind, ops } of kinds) {
      for (const op of ops) {
        try {
          const command = buildCommand(schema, op.fieldName, kind, op.description, service);
          if ((nameCounts.get(op.fieldName) ?? 0) > 1) {
            command.name(`${kebab(op.fieldName)}-${kind}`);
          }
          operations.push({ command, fieldName: op.fieldName, kind, service });
        } catch {
          // Field may not be resolvable against this schema — skip it.
        }
      }
    }
  }

  return operations.sort((a, b) => a.command.name().localeCompare(b.command.name()));
}

/** Rendered by `sl operations`: every flattened command, grouped by kind. */
export function formatOperationList(operations: readonly OperationCommand[], search?: string): string {
  const needle = search?.toLowerCase();
  const matching = needle
    ? operations.filter((op) => op.command.name().includes(needle) || op.service.toLowerCase().includes(needle))
    : operations;

  if (matching.length === 0) {
    return search
      ? `No operation matching "${search}".`
      : 'No operations available. The endpoint is not a composed supergraph, so no subgraph mapping is exposed.';
  }

  const lines: string[] = [];
  for (const kind of ['query', 'mutation'] as const) {
    const group = matching.filter((op) => op.kind === kind);
    if (group.length === 0) continue;

    const width = Math.max(...group.map((op) => op.command.name().length));
    lines.push(`${kind === 'query' ? 'Queries' : 'Mutations'}:`);
    for (const op of group) {
      lines.push(`  ${op.command.name().padEnd(width)}  ${op.service}`);
    }
    lines.push('');
  }

  return lines.join('\n');
}
